import { useState, useEffect, useRef } from "react";
import { 
  ChevronDown, 
  Check, 
  X, 
  AlertCircle, 
  Clock, 
  FileText, 
  FileSpreadsheet,
  Info,
  MessageSquare,
  Shield,
  TestTube,
  RefreshCw,
  ClipboardList,
  Zap,
  Upload
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTemplateValidation } from "@/hooks/use-template-validation";

interface ChecklistItem {
  id: string;
  name: string;
  status: 'ready' | 'pending' | 'failed' | 'loading';
  details?: string;
  icon: React.ReactNode;
  validationReport?: ValidationReportData | null;
  templateId?: string;
}

interface ValidationReportData {
  valid: boolean;
  coverage_percent: number;
  critical_missing: string[];
  missing_in_file: string[];
  warnings: string[];
}

interface PromptData {
  prompt_type: string;
  is_active: boolean;
}

interface TemplateData {
  id: string;
  filename: string;
  is_active: boolean;
  validation_status: string | null;
  validation_report: ValidationReportData | null;
  variables: unknown;
}

interface ValidationRulesData {
  id: string;
}

interface BootstrapLogData {
  status: string;
  test_results: {
    generation_success?: boolean;
    exports_tested?: Record<string, {
      success: boolean;
      quality_check?: {
        passed: boolean;
        unreplaced_placeholders?: string[];
      };
    }>;
  } | null;
  completed_at: string | null;
}

interface GeneratorActivationChecklistProps {
  generatorId: string;
  generatorSlug: string;
}

export function GeneratorActivationChecklist({ 
  generatorId, 
  generatorSlug 
}: GeneratorActivationChecklistProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadTarget, setUploadTarget] = useState<{ templateId: string; filename: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { validateAllTemplates, isValidating } = useTemplateValidation();

  const handleTemplateUpload = async (file: File, templateId: string) => {
    setIsUploading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      const contentType = file.name.endsWith('.xlsx')
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      const { data, error } = await supabase.functions.invoke('upload-template', {
        body: { templateId, fileBase64: base64, contentType },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Upload failed');

      toast.success(`Uploaded ${file.name} (${(file.size / 1024).toFixed(0)}KB)`);
      await fetchChecklistData();
    } catch (err) {
      toast.error(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadTarget(null);
    }
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    await handleTemplateUpload(file, uploadTarget.templateId);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const triggerUpload = (templateId: string, filename: string) => {
    setUploadTarget({ templateId, filename });
    fileInputRef.current?.click();
  };

  const fetchChecklistData = async () => {
    if (!generatorId) return;
    
    setIsLoading(true);
    
    try {
      // Fetch all data in parallel
      const [
        { data: prompts },
        { data: templates },
        { data: validationRules },
        { data: bootstrapLogs },
        { data: generatorData }
      ] = await Promise.all([
        supabase
          .from("generator_prompts")
          .select("prompt_type, is_active")
          .eq("generator_id", generatorId)
          .eq("is_active", true),
        supabase
          .from("generator_templates")
          .select("id, filename, is_active, validation_status, validation_report, variables")
          .eq("generator_id", generatorId)
          .eq("is_active", true),
        supabase
          .from("generator_validation_rules")
          .select("id")
          .eq("generator_id", generatorId),
        supabase
          .from("generator_bootstrap_logs")
          .select("status, test_results, completed_at")
          .eq("generator_id", generatorId)
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("generators")
          .select("about_what_is, about_how_it_works")
          .eq("id", generatorId)
          .single()
      ]);

      const promptsData = (prompts || []) as PromptData[];
      // Parse validation_report from JSON properly - cast through unknown
      const templatesData = (templates || []).map((t) => ({
        ...t,
        validation_report: t.validation_report as unknown as ValidationReportData | null
      })) as TemplateData[];
      setTemplates(templatesData);
      const rulesData = (validationRules || []) as ValidationRulesData[];
      const latestBootstrap = (bootstrapLogs?.[0] || null) as BootstrapLogData | null;

      // Build checklist items
      const items: ChecklistItem[] = [];

      // 1. System Prompt
      const hasSystemPrompt = promptsData.some(p => p.prompt_type === 'system');
      items.push({
        id: 'system-prompt',
        name: 'System Prompt',
        status: hasSystemPrompt ? 'ready' : 'pending',
        details: hasSystemPrompt ? 'Active' : 'Missing required system prompt',
        icon: <MessageSquare className="w-3.5 h-3.5" />
      });

      // 2. Generation Prompt
      const hasGenerationPrompt = promptsData.some(p => p.prompt_type === 'generation');
      items.push({
        id: 'generation-prompt',
        name: 'Generation Prompt',
        status: hasGenerationPrompt ? 'ready' : 'pending',
        details: hasGenerationPrompt ? 'Active' : 'Missing required generation prompt',
        icon: <MessageSquare className="w-3.5 h-3.5" />
      });

      // 3. Template Files
      const docxTemplate = templatesData.find(t => t.filename.toLowerCase().endsWith('.docx'));
      const xlsxTemplate = templatesData.find(t => t.filename.toLowerCase().endsWith('.xlsx'));
      const hasAnyTemplate = docxTemplate || xlsxTemplate;
      
      items.push({
        id: 'template-files',
        name: 'Template Files',
        status: hasAnyTemplate ? 'ready' : 'pending',
        details: hasAnyTemplate 
          ? `${[docxTemplate && 'DOCX', xlsxTemplate && 'XLSX'].filter(Boolean).join(', ')} available`
          : 'No active templates found',
        icon: docxTemplate ? <FileText className="w-3.5 h-3.5" /> : <FileSpreadsheet className="w-3.5 h-3.5" />
      });

      // 4. Template Validation - with enhanced details from validation_report
      const validTemplates = templatesData.filter(t => t.validation_status === 'valid');
      const invalidTemplates = templatesData.filter(t => t.validation_status === 'invalid');
      const pendingTemplates = templatesData.filter(t => !t.validation_status || t.validation_status === 'pending');
      
      let templateValidationStatus: 'ready' | 'pending' | 'failed' = 'pending';
      let templateValidationDetails = '';
      
      if (invalidTemplates.length > 0) {
        templateValidationStatus = 'failed';
        // Get detailed info from validation_report
        const firstInvalid = invalidTemplates[0];
        const report = firstInvalid.validation_report;
        if (report && report.critical_missing && report.critical_missing.length > 0) {
          const missingPreview = report.critical_missing.slice(0, 3).join(', ');
          const moreCount = report.critical_missing.length > 3 ? ` +${report.critical_missing.length - 3} more` : '';
          templateValidationDetails = `Missing: ${missingPreview}${moreCount} (${report.coverage_percent}% coverage)`;
        } else if (report) {
          templateValidationDetails = `${invalidTemplates.length} template(s) failed (${report.coverage_percent}% coverage)`;
        } else {
          templateValidationDetails = `${invalidTemplates.length} template(s) failed validation`;
        }
      } else if (validTemplates.length > 0 && pendingTemplates.length === 0) {
        templateValidationStatus = 'ready';
        // Show coverage if available
        const avgCoverage = validTemplates.reduce((sum, t) => {
          return sum + (t.validation_report?.coverage_percent || 100);
        }, 0) / validTemplates.length;
        templateValidationDetails = `${validTemplates.length} template(s) validated (${Math.round(avgCoverage)}% coverage)`;
      } else if (pendingTemplates.length > 0) {
        templateValidationStatus = 'pending';
        templateValidationDetails = `${pendingTemplates.length} template(s) need validation`;
      } else {
        templateValidationDetails = 'No templates to validate';
      }

      items.push({
        id: 'template-validation',
        name: 'Template Validation',
        status: templateValidationStatus,
        details: templateValidationDetails,
        icon: <Shield className="w-3.5 h-3.5" />
      });

      // 5. Validation Rules
      const hasValidationRules = rulesData.length > 0;
      items.push({
        id: 'validation-rules',
        name: 'Validation Rules',
        status: hasValidationRules ? 'ready' : 'pending',
        details: hasValidationRules ? 'Configured' : 'No validation rules defined',
        icon: <ClipboardList className="w-3.5 h-3.5" />
      });

      // 6. Bootstrap Test
      let bootstrapStatus: 'ready' | 'pending' | 'failed' = 'pending';
      let bootstrapDetails = 'No bootstrap test run';
      
      if (latestBootstrap) {
        const testResults = latestBootstrap.test_results;
        const generationSuccess = testResults?.generation_success;
        const exportsSuccess = testResults?.exports_tested && 
          Object.values(testResults.exports_tested).every(e => e.success);
        const qualitySuccess = testResults?.exports_tested && 
          Object.values(testResults.exports_tested).every(e => e.quality_check?.passed !== false);

        if (latestBootstrap.status === 'completed' && generationSuccess && exportsSuccess && qualitySuccess) {
          bootstrapStatus = 'ready';
          bootstrapDetails = 'All tests passed';
        } else if (latestBootstrap.status === 'testing' || latestBootstrap.status === 'running') {
          bootstrapStatus = 'pending';
          bootstrapDetails = 'Test in progress...';
        } else if (latestBootstrap.status === 'failed' || latestBootstrap.status === 'test_failed' || 
                   latestBootstrap.status === 'quality_failed' || latestBootstrap.status === 'validation_failed') {
          bootstrapStatus = 'failed';
          bootstrapDetails = `Status: ${latestBootstrap.status.replace('_', ' ')}`;
        } else {
          bootstrapStatus = generationSuccess ? 'ready' : 'failed';
          bootstrapDetails = generationSuccess ? 'Generation passed' : 'Generation failed';
        }
      }

      items.push({
        id: 'bootstrap-test',
        name: 'Bootstrap Test',
        status: bootstrapStatus,
        details: bootstrapDetails,
        icon: <TestTube className="w-3.5 h-3.5" />
      });

      // 7. About Content
      const hasAboutWhatIs = !!generatorData?.about_what_is?.trim();
      const hasAboutHowItWorks = !!generatorData?.about_how_it_works?.trim();
      const aboutReady = hasAboutWhatIs && hasAboutHowItWorks;
      items.push({
        id: 'about-content',
        name: 'About Content',
        status: aboutReady ? 'ready' : 'pending',
        details: aboutReady ? 'Both sections populated' : 'Missing about content',
        icon: <Info className="w-3.5 h-3.5" />
      });

      setChecklist(items);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Failed to fetch checklist data:", err);
      toast.error("Failed to load checklist");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded && generatorId) {
      fetchChecklistData();
    }
  }, [isExpanded, generatorId]);

  const readyCount = checklist.filter(c => c.status === 'ready').length;
  const totalCount = checklist.length;
  const allReady = readyCount === totalCount && totalCount > 0;
  const hasFailed = checklist.some(c => c.status === 'failed');

  const getStatusIcon = (status: ChecklistItem['status']) => {
    switch (status) {
      case 'ready':
        return <Check className="w-3.5 h-3.5 text-primary" />;
      case 'failed':
        return <X className="w-3.5 h-3.5 text-destructive" />;
      case 'pending':
        return <Clock className="w-3.5 h-3.5 text-muted-foreground" />;
      case 'loading':
        return <RefreshCw className="w-3.5 h-3.5 text-muted-foreground animate-spin" />;
    }
  };

  const getStatusColor = (status: ChecklistItem['status']) => {
    switch (status) {
      case 'ready':
        return 'text-primary';
      case 'failed':
        return 'text-destructive';
      case 'pending':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-sm">Activation Checklist</span>
          {totalCount > 0 && (
            <Badge 
              variant={allReady ? "default" : hasFailed ? "destructive" : "secondary"} 
              className="text-xs py-0 h-5"
            >
              {readyCount}/{totalCount}
            </Badge>
          )}
        </div>
        {isExpanded && (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border px-3 py-2.5 space-y-2 text-xs">
          {/* Generator Info */}
          <div className="flex items-center justify-between text-[10px] text-muted-foreground pb-1.5 border-b border-border">
            <span>Generator: <code className="bg-muted px-1 py-0.5 rounded">{generatorSlug}</code></span>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchChecklistData();
                    }}
                    disabled={isLoading}
                    className="h-5 w-5 p-0"
                  >
                    <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  Refresh checklist
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Checklist Items */}
          {isLoading && checklist.length === 0 ? (
            <div className="py-2 text-center text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1" />
              Loading...
            </div>
          ) : (
            <TooltipProvider delayDuration={200}>
              <div className="space-y-1">
                {checklist.map(item => (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted/30 transition-colors cursor-help">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{item.icon}</span>
                          <span className={item.status === 'ready' ? '' : getStatusColor(item.status)}>
                            {item.name}
                          </span>
                        </div>
                        {getStatusIcon(item.status)}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="text-xs max-w-[200px]">
                      {item.details}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          )}

          {/* Summary */}
          {!isLoading && checklist.length > 0 && (
            <div className="pt-2 border-t border-border space-y-2">
              {allReady ? (
                <div className="flex items-center gap-1.5 text-primary">
                  <Check className="w-3.5 h-3.5" />
                  <span>Ready for activation</span>
                </div>
              ) : hasFailed ? (
                <div className="flex items-center gap-1.5 text-destructive">
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span>Issues need to be resolved</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{totalCount - readyCount} item(s) pending</span>
                </div>
              )}
              
              {/* Template Upload Buttons - one per template */}
              {templates.length > 0 && (
                <div className="space-y-1">
                  {templates.map(t => (
                    <Button
                      key={t.id}
                      variant="outline"
                      size="sm"
                      className="w-full h-7 text-xs gap-1.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        triggerUpload(t.id, t.filename);
                      }}
                      disabled={isUploading || isLoading}
                    >
                      {isUploading && uploadTarget?.templateId === t.id ? (
                        <>
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-3 h-3" />
                          Replace {t.filename.split('.').pop()?.toUpperCase()} Template
                        </>
                      )}
                    </Button>
                  ))}
                </div>
              )}

              {/* Validate Now Button - shown when templates need validation */}
              {templates.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-xs gap-1.5"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const result = await validateAllTemplates(generatorId);
                    if (result.allValid || result.results.length > 0) {
                      await fetchChecklistData();
                    }
                  }}
                  disabled={isValidating || isLoading}
                >
                  {isValidating ? (
                    <>
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <Zap className="w-3 h-3" />
                      Validate Templates
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,.xlsx"
            className="hidden"
            onChange={onFileSelected}
          />

          {/* Last Refresh */}
          {lastRefresh && (
            <div className="text-[10px] text-muted-foreground text-right">
              Updated {lastRefresh.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
